import { useQuery } from '@apollo/client';
import { GridEventListener, useGridApiRef, useKeepGroupedColumnsHidden } from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useState } from 'react';

import GET_SEIZURES_PAGED from '../../../../api/apollo/query/GetSeizuresPaged';
import { useAuth } from '../../../../api/auth';
import { CustomDataGrid } from '../../../../components/CustomDataGrid';
import TableToolbar from '../../../../components/Table/TableToolbar';
import { DefaultTableProps } from '../../../../components/Table/types';
import { Officer, Seizure } from '../../../../types';
import { useSummaryTableData } from '../../SummaryTableDataContext';
import columns from './columns';

export interface SeizureDataGrid {
    id: string;
    rowId: string;
    docket: string | null;
    date: string | null;
    locationName: string | null;
    administrativeLevel1: string | null;
    arrestingOfficers?: Officer[];
    seizedCommodityId: string;
    seizedCommodityCommodity?: string;
    seizedCommodityProtectionLevel?: string | null;
    seizedCommodityCount: number | null;
    seizedCommodityWeightGrams: number | null;
    seizedCommodityValue: number | null;
    seizedCommodityValueCurrency: string | null;
    origin: string | null;
}

// const getLocationString = (value: Location | null) => {
//     let result = '';
//     if (value?.name) {
//         result += value?.name;
//     }

//     if (value?.administrativeLevel1) {
//         result += result.length > 0 ? `, ${value?.administrativeLevel1.name}` : value?.administrativeLevel1.name;
//     }
//     return result;
// };

const prepareSeizures = (seizures: Seizure[]) => {
    const result: SeizureDataGrid[] = [];

    seizures.forEach((item) => {
        item.seizedCommodities.forEach((commodity) => {
            result.push({
                id: item.id,
                rowId: `${item.id} - ${commodity.id}`,
                docket: item.courtCases ? item.courtCases[0]?.docket : null,
                date: item.date,
                locationName: item.locationName || null,
                administrativeLevel1: item.administrativeLevel1 || null,
                arrestingOfficers: item.officers,
                seizedCommodityId: commodity.id,
                seizedCommodityCommodity: ((commodity.species?.commonName || '') + ' ' + (commodity.unit || '')).trim(),
                seizedCommodityProtectionLevel: commodity.protectionLevel?.label ?? null,
                seizedCommodityCount: commodity.count,
                seizedCommodityWeightGrams: commodity.weightGrams,
                seizedCommodityValue: commodity.value,
                seizedCommodityValueCurrency: commodity.valueCurrency,
                origin: commodity.originName,
            });
        });
    });
    return result;
};

export const Seizures = ({ shrinkTitle, hideHeaders, showHeaders }: DefaultTableProps) => {
    const { user } = useAuth();
    const apiRef = useGridApiRef();
    const { tableData, setTableRows, appendTableRows } = useSummaryTableData();
    const [isFetchingAllPages, setIsFetchingAllPages] = useState(false);
    const rows = (tableData['seizures'] ?? []) as SeizureDataGrid[];

    const PAGE_SIZE = 1000;

    const { loading, fetchMore } = useQuery(GET_SEIZURES_PAGED, {
        variables: { first: PAGE_SIZE },
        skip: tableData['seizures'] != null,
        onCompleted: ({ seizuresPaged }) => {
            const nodes = seizuresPaged.edges.map(({ node }) => node);
            setTableRows('seizures', prepareSeizures(nodes));

            const fetchAllPages = async (hasNextPage: boolean, endCursor: string) => {
                if (!hasNextPage) return;
                const { data } = await fetchMore({
                    variables: { first: PAGE_SIZE, after: endCursor },
                });
                const moreNodes = data.seizuresPaged.edges.map(({ node }) => node);
                appendTableRows('seizures', prepareSeizures(moreNodes));
                await fetchAllPages(data.seizuresPaged.pageInfo.hasNextPage, data.seizuresPaged.pageInfo.endCursor);
            };

            if (seizuresPaged.pageInfo.hasNextPage) {
                setIsFetchingAllPages(true);
                fetchAllPages(seizuresPaged.pageInfo.hasNextPage, seizuresPaged.pageInfo.endCursor).finally(() =>
                    setIsFetchingAllPages(false)
                );
            }
        },
    });

    useEffect(() => {
        const handleEvent: GridEventListener<'rowsScroll'> = (params) => {
            params.top > 5 ? hideHeaders() : showHeaders();
        };

        apiRef.current.subscribeEvent('rowsScroll', handleEvent);
    }, [apiRef, hideHeaders, showHeaders]);

    const getAggregationPosition = useCallback((groupNode) => (groupNode == null ? 'footer' : 'inline'), []);

    const initialState = useKeepGroupedColumnsHidden({
        apiRef,
        initialState: {
            aggregation: {
                model: {
                    seizedCommodityCount: 'sum',
                    seizedCommodityWeightKg: 'sum',
                    seizedCommodityValue: 'sum',
                },
            },
        },
    });

    return (
        <CustomDataGrid
            apiRef={apiRef}
            getRowId={(row) => row.rowId}
            columns={columns}
            rows={rows}
            checkboxSelection={false}
            aggregationRowsScope="all"
            getAggregationPosition={getAggregationPosition}
            rowGroupingColumnMode="single"
            loading={loading || isFetchingAllPages}
            hideFooterSelectedRowCount
            components={{ Toolbar: TableToolbar }}
            componentsProps={{
                toolbar: {
                    title: 'Seizures',
                    showTitle: shrinkTitle,
                    csvExportEnabled:
                        !loading && !isFetchingAllPages && user?.permissions?.map(({ codename }) => codename).includes('can_export_tables'),
                    tableApi: apiRef.current,
                    excelExportEnabled: false,
                },
            }}
            initialState={initialState}
            experimentalFeatures={{ aggregation: true }}
        />
    );
};

export default Seizures;
